import type { ReactElement } from 'react'
import {
  RadarChart as RechartsRadarChart,
  Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from 'recharts'

export interface RadarChartDataPoint {
  stat: string
  value: number
  baseline?: number
}

export interface RadarChartTickPayload {
  value: string
  index: number
  coordinate: number
}

export interface RadarChartTickProps {
  payload: RadarChartTickPayload
  x: number
  y: number
  cx: number
  cy: number
  index: number
}

export interface RadarChartProps {
  data: RadarChartDataPoint[]
  height?: number
  color?: string
  baselineColor?: string
  name?: string
  baselineName?: string
  showBaseline?: boolean
  maxValue?: number
  /** Custom tick renderer for axis labels — receives position/payload, returns SVG element */
  renderTick?: (props: RadarChartTickProps) => ReactElement
  /** Whether to show the legend when a baseline is present (default: true) */
  showLegend?: boolean
}

export function RadarChart({
  data,
  height = 300,
  color = 'var(--nexus-purple)',
  baselineColor = 'var(--nexus-text-muted)',
  name = 'Player',
  baselineName = 'Average',
  showBaseline = true,
  maxValue,
  renderTick,
  showLegend = true,
}: RadarChartProps) {
  if (data.length === 0) return null

  const hasBaseline = showBaseline && data.some((d) => d.baseline !== undefined)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts tick typing is overly restrictive
  const tickValue: any = renderTick ?? { fill: 'var(--nexus-text-muted)', fontSize: 12 }

  return (
    <div style={{ outline: 'none' }} tabIndex={-1}>
    <ResponsiveContainer width="100%" height={height}>
      <RechartsRadarChart data={data} cx="50%" cy="50%" outerRadius="75%" style={{ outline: 'none', cursor: 'default' }}>
        <PolarGrid stroke="var(--nexus-border)" />
        <PolarAngleAxis
          dataKey="stat"
          tick={tickValue}
        />
        <PolarRadiusAxis
          angle={90}
          domain={[0, maxValue ?? 'auto']}
          tick={false}
          axisLine={false}
        />
        {hasBaseline && (
          <Radar
            name={baselineName}
            dataKey="baseline"
            stroke={baselineColor}
            fill={baselineColor}
            fillOpacity={0.1}
            strokeDasharray="4 4"
            dot={false}
            activeDot={false}
          />
        )}
        <Radar
          name={name}
          dataKey="value"
          stroke={color}
          fill={color}
          fillOpacity={0.25}
          strokeWidth={2}
          dot={{ r: 3, fill: color, stroke: color }}
          activeDot={{ r: 5, fill: color, stroke: 'var(--nexus-bg)', strokeWidth: 2 }}
        />
        <Tooltip
          cursor={false}
          contentStyle={{
            background: 'var(--nexus-surface)',
            border: '1px solid var(--nexus-border)',
            borderRadius: 'var(--nexus-radius-surface)',
            fontSize: 12,
            color: 'var(--nexus-text)',
          }}
          formatter={(value, tooltipName) => [
            `${Math.round(Number(value))}`,
            String(tooltipName),
          ]}
          labelStyle={{ color: 'var(--nexus-text-muted)', fontWeight: 600 }}
        />
        {hasBaseline && showLegend && (
          <Legend
            wrapperStyle={{ fontSize: 12, color: 'var(--nexus-text-muted)' }}
          />
        )}
      </RechartsRadarChart>
    </ResponsiveContainer>
    </div>
  )
}
